// src/App.jsx
import React, { useCallback, useState, useEffect, useMemo } from 'react';
import './components/GradientText.css'; // Existing import
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useSession, useSupabaseClient, SessionContextProvider } from '@supabase/auth-helpers-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from './supabaseClient';
import { createInitialUserStatus, markInitialTestResultAsRead, clearUserErrorStatus, getFriendRequestsFrontend, getFriendships, getPublicSetting } from './api/supabaseAPI'; 
import { SpeedInsights } from "@vercel/speed-insights/react";
import FullScreenLoader from './components/FullScreenLoader';
import GlobalErrorOverlay from './components/GlobalErrorOverlay';
import { Analytics } from "@vercel/analytics/react"

import Login from './pages/Login';
import Introduction from './pages/Introduction';
import Practice from './pages/Practice';
import CustomPractice from './pages/CustomPractice';
import Records from './pages/Records';
import Overall from './pages/Overall';
import Profile from './pages/Profile';
import Friends from './pages/Friends';
import Leaderboard from './pages/Leaderboard';
import Shop from './pages/Shop';
import InitialTest from './pages/InitialTest';
import Courses from './pages/Courses';
import CoursePractice from './pages/CoursePractice';
import TutorApplication from './pages/TutorApplication';
import TutorsList from './pages/TutorsList';
import TutorModeSelection from './pages/TutorModeSelection';
import TutorWorkspace from './pages/TutorWorkspace'; // ✨ Import
import TutorProfile from './pages/TutorProfile'; // ✨ Import
import PublicTutorProfile from './pages/PublicTutorProfile'; // ✨ Import
import AdminDashboard from './pages/AdminDashboard';
import ClickSpark from './components/ClickSpark';
import AuthCallback from './pages/AuthCallback';
import MainLayout from './layouts/MainLayout';
import PrivateRoute from './routes/PrivateRoute';
import useUser from './hooks/useUser';
import useUserRoles from './hooks/useUserRoles'; // ✨ New Hook
import useProfileMediaRealtime from './hooks/useProfileMediaRealtime';
import useFriendRequestsRealtime from './hooks/useFriendRequestsRealtime';
import useFriendshipsRealtime from './hooks/useFriendshipsRealtime';

function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const session = useSession();
  const supabaseClient = useSupabaseClient();
  const queryClient = useQueryClient();
  const { data: r2PublicBaseUrl } = useQuery({
    queryKey: ['public_settings', 'r2_public_base_url'],
    queryFn: () => getPublicSetting('r2_public_base_url'),
    staleTime: 1000 * 60 * 30,
    refetchOnMount: false,
  });
  const publicBaseUrl = useMemo(() => {
    const rawBaseUrl = (import.meta.env.VITE_R2_PUBLIC_BASE_URL || r2PublicBaseUrl || '').replace(/\/$/, '');
    return rawBaseUrl ? rawBaseUrl.replace(/\/$/, '') : '';
  }, [r2PublicBaseUrl]);

  // ✨ Theme Initialization
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme');
    const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    
    if (savedTheme) {
      document.documentElement.setAttribute('data-theme', savedTheme);
    } else if (systemPrefersDark) {
      document.documentElement.setAttribute('data-theme', 'dark');
    }
    // If no preference and system is light, default is light (no attribute needed)
  }, []);

  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [isDismissingError, setIsDismissingError] = useState(false); // ✨ [新增] 樂觀更新狀態

  const { data: profileData, isLoading: isLoadingProfile, isError } = useUser();
  const { data: userRoles } = useUserRoles(); // ✨ Fetch roles independently

  const userSettings = useMemo(() => profileData?.settings || {}, [profileData]);
  const practiceLanguage = userSettings?.language || 'en_us';
  const userStatus = useMemo(() => {
    if (!profileData?.status) return {};
    return profileData.status.find(s => s.language === practiceLanguage) || {};
  }, [profileData, practiceLanguage]);
  
  const combinedUserData = useMemo(() => {
    if (!session || !profileData) return null;
    return { 
      ...profileData, 
      email: session.user.email,
      settings: userSettings,
      status: userStatus,
      googleAvatarUrl: session.user?.user_metadata?.avatar_url // ✨ Pass Google Avatar URL
    };
  }, [session, profileData, userSettings, userStatus]);
  
  useProfileMediaRealtime({ userData: combinedUserData, publicBaseUrl });

  const initialTestResult = userStatus?.init_test_result;
  const showInitialTestReport = initialTestResult && initialTestResult.isRead === false;

  const hasCompletedTest = !!userStatus?.sug_lvl && !showInitialTestReport;
  
  // 【核心新增】全域錯誤狀態
  const errorStatus = userSettings?.error_status;

  // ✨ [新增] 當資料庫中的錯誤狀態清除後，重置本地的隱藏狀態
  useEffect(() => {
    if (!errorStatus) {
      setIsDismissingError(false);
    }
  }, [errorStatus]);

  const handleClearError = async () => {
    // ✨ 立即隱藏視窗 (樂觀更新)
    setIsDismissingError(true);
    
    try {
      await clearUserErrorStatus(profileData.id);
      // 在背景刷新數據
      queryClient.invalidateQueries({ queryKey: ['user', profileData.id] });
    } catch (err) {
      console.error("Failed to clear global error:", err);
      // 如果失敗，則恢復顯示以便使用者重試
      setIsDismissingError(false);
    }
  };

  // ✨ 【【【核心修正】】】 ✨
  //    這個 useEffect 專門用來處理新語言的 user_status 記錄創建
  useEffect(() => {
    // 確保我們有必要的數據，並且不是在載入中
    if (isLoadingProfile || !profileData || !practiceLanguage) {
      return;
    }

    // 檢查 userStatus 是否為空物件（通過檢查它是否有 id 屬性來判斷）
    // 這意味著對於當前的 practiceLanguage，資料庫中沒有對應的記錄
    if (!userStatus?.id) {
      console.log(`No user_status found for language "${practiceLanguage}". Creating one...`);
      
      // 呼叫 API 創建新的記錄
      createInitialUserStatus(profileData.id, practiceLanguage)
        .then(() => {
          console.log(`Successfully created user_status for "${practiceLanguage}". Invalidating user query...`);
          // 成功創建後，立即讓 'user' query 失效，以重新獲取包含新 status 的完整數據
          queryClient.invalidateQueries({ queryKey: ['user', profileData.id] });
        })
        .catch(error => {
          console.error(`Failed to create user_status for language "${practiceLanguage}":`, error);
        });
    }
    
    // 這個 effect 的依賴項是關鍵：
    // - isLoadingProfile: 確保在數據載入完成後才執行
    // - profileData: 確保我們有 user ID
    // - practiceLanguage: 當語言切換時，觸發此 effect
    // - userStatus: 當 status 數據變化時，也觸發此 effect
  }, [isLoadingProfile, profileData, practiceLanguage, userStatus, queryClient]);


  useEffect(() => {
    // ✨ 【【【核心修正】】】 ✨
    // 檢查 URL hash 是否包含 'type=recovery'。
    // 因為 location 在依賴項中，所以每次 URL 變化都會觸發此檢查。
    if (location.hash.includes('type=recovery')) {
      setShowPasswordReset(true);
    }

    const { data: { subscription } } = supabaseClient.auth.onAuthStateChange((event, session) => {
      // 舊的 onAuthStateChange 監聽器仍然保留，以處理來自其他分頁的廣播事件
      if (event === 'PASSWORD_RECOVERY') {
        setShowPasswordReset(true);
      }
      if (event === 'SIGNED_OUT') {
        queryClient.clear();
        navigate('/login');
      }
    });
    
    return () => subscription.unsubscribe();

  // 將 location 加入依賴項陣列，這是解決問題的關鍵！
  }, [supabaseClient, navigate, queryClient, location]);

  useEffect(() => {
    // 1. 確保我們有 user ID，否則無法建立個人化的訂閱
    const userId = session?.user?.id;
    if (!userId) {
      return;
    }

    // 2. 創建一個 Realtime 訂閱實例
    const channel = supabaseClient
      .channel(`user-status-changes-for-${userId}`) // 為每個用戶創建一個唯一的頻道名稱
      .on(
        'postgres_changes', 
        { event: 'UPDATE', schema: 'public', table: 'user_status', filter: `user_id=eq.${userId}` },
        (payload) => {
          console.log('🔵 Realtime: user_status updated:', payload);
          queryClient.invalidateQueries({ queryKey: ['user', userId] });
        }
      )
      .on(
        'postgres_changes', 
        { event: 'UPDATE', schema: 'public', table: 'user_settings', filter: `user_id=eq.${userId}` },
        (payload) => {
          console.log('🔴 Realtime: user_settings updated (Instant Cache Update):', payload);
          // ✨ 核心優化：直接將 Realtime 推送的新數據寫入快取，跳過網路請求延遲
          if (payload.new) {
            queryClient.setQueryData(['user', userId], (oldData) => {
              if (!oldData) return oldData;
              return {
                ...oldData,
                settings: {
                  ...oldData.settings,
                  ...payload.new // 直接合併新的設定 (包含 error_status)
                }
              };
            });
          }
        }
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'profiles', filter: `id=eq.${userId}` },
        (payload) => {
          console.log('🟣 Realtime: profiles updated (XP/Coins):', payload);
          if (payload.new) {
            queryClient.setQueryData(['user', userId], (oldData) => {
              if (!oldData) return oldData;
              return {
                ...oldData,
                ...payload.new,
              };
            });
          }
        }
      )
      .on(
        'postgres_changes', 
        { event: 'INSERT', schema: 'public', table: 'courses', filter: `uid=eq.${userId}` },
        (payload) => {
          console.log('🟢 Realtime: New course generated:', payload);
          queryClient.invalidateQueries({ queryKey: ['courses', userId] });
        }
      )
      .subscribe((status, err) => {
        // 訂閱狀態的回呼，方便偵錯
        if (status === 'SUBSCRIBED') {
          console.log('✅ Realtime: 成功訂閱 user_status 表的更新！');
        }
        if (status === 'CHANNEL_ERROR') {
          console.error('❌ Realtime: 訂閱失敗:', err);
        }
      });

    // 5. 清理函數：當元件卸載或 user ID 變化時，取消訂閱，防止內存洩漏
    return () => {
      console.log('⚪️ Realtime: 取消訂閱 user_status 表。');
      supabaseClient.removeChannel(channel);
    };

  // 依賴項：確保只有在 supabaseClient 和 session 準備好後才執行
  }, [supabaseClient, session, queryClient]);

  useEffect(() => {
    const userId = session?.user?.id;
    if (!userId) return;
    queryClient.prefetchQuery({
      queryKey: ['friend_requests', userId],
      queryFn: () => getFriendRequestsFrontend(userId),
      staleTime: 1000 * 30,
    });
    queryClient.prefetchQuery({
      queryKey: ['friendships', userId],
      queryFn: () => getFriendships(userId),
      staleTime: 1000 * 30,
    });
  }, [session?.user?.id, queryClient]);

  // 全局 Realtime hooks for friends
  useFriendRequestsRealtime(session?.user?.id);
  useFriendshipsRealtime(session?.user?.id);

  const handleLogout = useCallback(async () => {
    console.log('Vercel supabaseClient object:', supabaseClient);
    localStorage.removeItem('theralingua_topicSelectorDraft');
    await supabaseClient.auth.signOut();
  }, [supabaseClient]);

  const onTestComplete = useCallback(() => {
    console.log("onTestComplete called, but navigation is now handled by the report modal.");
  }, []);

  const handlePasswordUpdated = () => {
    // 1. 隱藏密碼重設的 UI
    setShowPasswordReset(false);
    
    // 2. ✨ 【【【核心修正】】】 ✨
    //    不再呼叫 handleLogout()，而是直接導航到 Profile 頁面
    //    讓使用者可以看到他們的個人資料，確認他們仍然是登入狀態
    navigate('/profile');

    // 3. （可選，但強烈建議）為了確保 UI 狀態的完全同步，作廢 'user' query
    //    這會讓 MiniProfile 等元件顯示最新的數據
    queryClient.invalidateQueries({ queryKey: ['user', session?.user?.id] });
  };

  const handleReportRead = async () => {
    if (!session?.user?.id || !practiceLanguage || !initialTestResult) return;
    try {
      await markInitialTestResultAsRead(session.user.id, practiceLanguage, initialTestResult);
      queryClient.invalidateQueries({ queryKey: ['user', session.user.id] });
      navigate('/introduction');
    } catch (error) {
      console.error("Failed to mark report as read:", error);
      queryClient.invalidateQueries({ queryKey: ['user', session.user.id] });
    }
  };

  if (session && isLoadingProfile) return <FullScreenLoader />;
  if (isError) {
    return (
      <div style={{ padding: 20, textAlign: 'center', color: 'red' }}>
        <p>Error: Failed to load user profile.</p>
        <button onClick={handleLogout}>Logout and try again</button>
      </div>
    );
  }

  // --- ✨ 核心修正 3: 將 ClickSpark 作為所有路由的頂層包裹器 ---
  return (
    <ClickSpark>
            <GlobalErrorOverlay
              errorStatus={!isDismissingError ? errorStatus : null}
              onClear={handleClearError}
              onNavigate={navigate}
            />      <Routes>
        {/* Login 頁面現在也被 ClickSpark 包裹 */}
        <Route path="/login" element={session ? <Navigate to={location.state?.from?.pathname || "/introduction"} replace /> : <Login />} />
        <Route path="/" element={<Navigate to="/introduction" replace />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        
        <Route
          path="/introduction"
          element={
            <PrivateRoute>
              <MainLayout
                showInitialTestReport={showInitialTestReport}
                initialTestResult={initialTestResult}
                onReportRead={handleReportRead}
                showPasswordReset={showPasswordReset}
                onPasswordUpdated={handlePasswordUpdated}
                onLogout={handleLogout}
                hasCompletedTest={hasCompletedTest}
                userData={combinedUserData}
                isAdmin={userRoles?.isAdmin}
                isTutor={userRoles?.isTutor}
              >
                <Introduction />
              </MainLayout>
            </PrivateRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <PrivateRoute>
              <MainLayout
                showInitialTestReport={showInitialTestReport}
                initialTestResult={initialTestResult}
                onReportRead={handleReportRead}
                showPasswordReset={showPasswordReset}
                onPasswordUpdated={handlePasswordUpdated}
                onLogout={handleLogout}
                hasCompletedTest={hasCompletedTest}
                userData={combinedUserData}
                isAdmin={userRoles?.isAdmin}
                isTutor={userRoles?.isTutor}
              >
                <Profile />
              </MainLayout>
            </PrivateRoute>
          }
        />
        
        <Route 
          path="/practice" 
          element={
            <PrivateRoute>
              <MainLayout
                showInitialTestReport={showInitialTestReport}
                initialTestResult={initialTestResult}
                onReportRead={handleReportRead}
                showPasswordReset={showPasswordReset}
                onPasswordUpdated={handlePasswordUpdated}
                onLogout={handleLogout}
                hasCompletedTest={hasCompletedTest}
                userData={combinedUserData}
                isAdmin={userRoles?.isAdmin}
                isTutor={userRoles?.isTutor}
              >
                <Practice 
                  practiceLanguage={practiceLanguage} 
                  userStatus={userStatus} 
                />
              </MainLayout>
            </PrivateRoute>
          } 
        />
        
        <Route 
          path="/records" 
          element={
            <PrivateRoute>
              <MainLayout
                showInitialTestReport={showInitialTestReport}
                initialTestResult={initialTestResult}
                onReportRead={handleReportRead}
                showPasswordReset={showPasswordReset}
                onPasswordUpdated={handlePasswordUpdated}
                onLogout={handleLogout}
                hasCompletedTest={hasCompletedTest}
                userData={combinedUserData}
                isAdmin={userRoles?.isAdmin}
                isTutor={userRoles?.isTutor}
              >
                <Records />
              </MainLayout>
            </PrivateRoute>
          } 
        />

        <Route 
          path="/friends" 
          element={
            <PrivateRoute>
              <MainLayout
                showInitialTestReport={showInitialTestReport}
                initialTestResult={initialTestResult}
                onReportRead={handleReportRead}
                showPasswordReset={showPasswordReset}
                onPasswordUpdated={handlePasswordUpdated}
                onLogout={handleLogout}
                hasCompletedTest={hasCompletedTest}
                userData={combinedUserData}
                isAdmin={userRoles?.isAdmin}
                isTutor={userRoles?.isTutor}
              >
                <Friends />
              </MainLayout>
            </PrivateRoute>
          } 
        />

        <Route 
          path="/leaderboard" 
          element={
            <PrivateRoute>
              <MainLayout
                showInitialTestReport={showInitialTestReport}
                initialTestResult={initialTestResult}
                onReportRead={handleReportRead}
                showPasswordReset={showPasswordReset}
                onPasswordUpdated={handlePasswordUpdated}
                onLogout={handleLogout}
                hasCompletedTest={hasCompletedTest}
                userData={combinedUserData}
                isAdmin={userRoles?.isAdmin}
                isTutor={userRoles?.isTutor}
              >
                <Leaderboard />
              </MainLayout>
            </PrivateRoute>
          } 
        />
        <Route 
          path="/practice/custom" 
          element={
            <PrivateRoute>
              <MainLayout
                showInitialTestReport={showInitialTestReport}
                initialTestResult={initialTestResult}
                onReportRead={handleReportRead}
                showPasswordReset={showPasswordReset}
                onPasswordUpdated={handlePasswordUpdated}
                onLogout={handleLogout}
                hasCompletedTest={hasCompletedTest}
                userData={combinedUserData}
                isAdmin={userRoles?.isAdmin}
                isTutor={userRoles?.isTutor}
              >
                <CustomPractice />
              </MainLayout>
            </PrivateRoute>
          } 
        />

        <Route 
          path="/shop" 
          element={
            <PrivateRoute>
              <MainLayout
                showInitialTestReport={showInitialTestReport}
                initialTestResult={initialTestResult}
                onReportRead={handleReportRead}
                showPasswordReset={showPasswordReset}
                onPasswordUpdated={handlePasswordUpdated}
                onLogout={handleLogout}
                hasCompletedTest={hasCompletedTest}
                userData={combinedUserData}
                isAdmin={userRoles?.isAdmin}
                isTutor={userRoles?.isTutor}
              >
                <Shop />
              </MainLayout>
            </PrivateRoute>
          } 
        />

        <Route 
          path="/overall" 
          element={
            <PrivateRoute>
              <MainLayout
                showInitialTestReport={showInitialTestReport}
                initialTestResult={initialTestResult}
                onReportRead={handleReportRead}
                showPasswordReset={showPasswordReset}
                onPasswordUpdated={handlePasswordUpdated}
                onLogout={handleLogout}
                hasCompletedTest={hasCompletedTest}
                userData={combinedUserData}
                isAdmin={userRoles?.isAdmin}
                isTutor={userRoles?.isTutor}
              >
                <Overall />
              </MainLayout>
            </PrivateRoute>
          } 
        />
        <Route 
          path="/tutor/workspace/:language" 
          element={
            <PrivateRoute>
              <MainLayout
                showInitialTestReport={showInitialTestReport}
                initialTestResult={initialTestResult}
                onReportRead={handleReportRead}
                showPasswordReset={showPasswordReset}
                onPasswordUpdated={handlePasswordUpdated}
                onLogout={handleLogout}
                hasCompletedTest={hasCompletedTest}
                userData={combinedUserData}
                isAdmin={userRoles?.isAdmin}
                isTutor={userRoles?.isTutor}
              >
                <TutorWorkspace />
              </MainLayout>
            </PrivateRoute>
          } 
        />

        <Route 
          path="/tutor/workspace/:language/profile" 
          element={
            <PrivateRoute>
              <MainLayout
                showInitialTestReport={showInitialTestReport}
                initialTestResult={initialTestResult}
                onReportRead={handleReportRead}
                showPasswordReset={showPasswordReset}
                onPasswordUpdated={handlePasswordUpdated}
                onLogout={handleLogout}
                hasCompletedTest={hasCompletedTest}
                userData={combinedUserData}
                isAdmin={userRoles?.isAdmin}
                isTutor={userRoles?.isTutor}
              >
                <TutorProfile />
              </MainLayout>
            </PrivateRoute>
          } 
        />
        
        <Route 
          path="/tutors" 
          element={
            <PrivateRoute>
              <MainLayout
                showInitialTestReport={showInitialTestReport}
                initialTestResult={initialTestResult}
                onReportRead={handleReportRead}
                showPasswordReset={showPasswordReset}
                onPasswordUpdated={handlePasswordUpdated}
                onLogout={handleLogout}
                hasCompletedTest={hasCompletedTest}
                userData={combinedUserData}
                isAdmin={userRoles?.isAdmin}
                isTutor={userRoles?.isTutor}
              >
                <TutorsList />
              </MainLayout>
            </PrivateRoute>
          } 
        />

        <Route 
          path="/tutor/:uid/:language" 
          element={
            <PrivateRoute>
              <MainLayout
                showInitialTestReport={showInitialTestReport}
                initialTestResult={initialTestResult}
                onReportRead={handleReportRead}
                showPasswordReset={showPasswordReset}
                onPasswordUpdated={handlePasswordUpdated}
                onLogout={handleLogout}
                hasCompletedTest={hasCompletedTest}
                userData={combinedUserData}
                isAdmin={userRoles?.isAdmin}
                isTutor={userRoles?.isTutor}
              >
                <PublicTutorProfile />
              </MainLayout>
            </PrivateRoute>
          } 
        />

        <Route 
          path="/tutor-dashboard" 
          element={
            <PrivateRoute>
              <MainLayout
                showInitialTestReport={showInitialTestReport}
                initialTestResult={initialTestResult}
                onReportRead={handleReportRead}
                showPasswordReset={showPasswordReset}
                onPasswordUpdated={handlePasswordUpdated}
                onLogout={handleLogout}
                hasCompletedTest={hasCompletedTest}
                userData={combinedUserData}
                isAdmin={userRoles?.isAdmin}
                isTutor={userRoles?.isTutor}
              >
                <TutorModeSelection />
              </MainLayout>
            </PrivateRoute>
          } 
        />
        
        <Route 
          path="/tutor-application"
          element={
            <PrivateRoute>
              <MainLayout
                showInitialTestReport={showInitialTestReport}
                initialTestResult={initialTestResult}
                onReportRead={handleReportRead}
                showPasswordReset={showPasswordReset}
                onPasswordUpdated={handlePasswordUpdated}
                onLogout={handleLogout}
                hasCompletedTest={hasCompletedTest}
                userData={combinedUserData}
                isAdmin={userRoles?.isAdmin}
                isTutor={userRoles?.isTutor}
              >
                <TutorApplication />
              </MainLayout>
            </PrivateRoute>
          } 
        />
        
        <Route 
          path="/initial-test" 
          element={
            <PrivateRoute>
              <MainLayout
                showInitialTestReport={showInitialTestReport}
                initialTestResult={initialTestResult}
                onReportRead={handleReportRead}
                showPasswordReset={showPasswordReset}
                onPasswordUpdated={handlePasswordUpdated}
                onLogout={handleLogout}
                hasCompletedTest={hasCompletedTest}
                userData={combinedUserData}
                isAdmin={userRoles?.isAdmin}
                isTutor={userRoles?.isTutor}
              >
                {hasCompletedTest ? (
                  <Navigate to="/courses" replace />
                ) : (
                  <InitialTest onTestComplete={onTestComplete} practiceLanguage={practiceLanguage} />
                )}
              </MainLayout>
            </PrivateRoute>
          } 
        />

        <Route 
          path="/courses" 
          element={
            <PrivateRoute>
              <MainLayout
                showInitialTestReport={showInitialTestReport}
                initialTestResult={initialTestResult}
                onReportRead={handleReportRead}
                showPasswordReset={showPasswordReset}
                onPasswordUpdated={handlePasswordUpdated}
                onLogout={handleLogout}
                hasCompletedTest={hasCompletedTest}
                userData={combinedUserData}
                isAdmin={userRoles?.isAdmin}
                isTutor={userRoles?.isTutor}
              >
                <Courses />
              </MainLayout>
            </PrivateRoute>
          } 
        />

        <Route 
          path="/course/:cid/practice" 
          element={
            <PrivateRoute>
              <MainLayout
                showInitialTestReport={showInitialTestReport}
                initialTestResult={initialTestResult}
                onReportRead={handleReportRead}
                showPasswordReset={showPasswordReset}
                onPasswordUpdated={handlePasswordUpdated}
                onLogout={handleLogout}
                hasCompletedTest={hasCompletedTest}
                userData={combinedUserData}
                isAdmin={userRoles?.isAdmin}
                isTutor={userRoles?.isTutor}
              >
                <CoursePractice />
              </MainLayout>
            </PrivateRoute>
          } 
        />
        
        <Route 
          path="/admin" 
          element={
            <PrivateRoute>
              <MainLayout
                showInitialTestReport={showInitialTestReport}
                initialTestResult={initialTestResult}
                onReportRead={handleReportRead}
                showPasswordReset={showPasswordReset}
                onPasswordUpdated={handlePasswordUpdated}
                onLogout={handleLogout}
                hasCompletedTest={hasCompletedTest}
                userData={combinedUserData}
                isAdmin={userRoles?.isAdmin}
                isTutor={userRoles?.isTutor}
              >
                <AdminDashboard />
              </MainLayout>
            </PrivateRoute>
          } 
        />
        
        <Route path="*" element={<p>Page Not Found</p>} />
      </Routes>
    </ClickSpark>
  );
}

// --- 帶有認證載入邏輯的 App 包裹器 (保持不變) ---
const AppWithAuth = () => {
  const session = useSession();
  const [initialLoad, setInitialLoad] = useState(true);

  useEffect(() => {
    if (session !== undefined) {
      const timer = setTimeout(() => setInitialLoad(false), 100);
      return () => clearTimeout(timer);
    }
  }, [session]);

  if (initialLoad) return <FullScreenLoader />;

  return <App />;
};

// --- 最終導出的根元件 (保持不變) ---
export default function AppWrapper() {
  return (
    <BrowserRouter>
      <SessionContextProvider supabaseClient={supabase}>
        <AppWithAuth />
        <SpeedInsights />
        <Analytics />
      </SessionContextProvider>
    </BrowserRouter>
  );
}
